import React, { useEffect } from "react";
import PageForm from "../../templates/PageForm";
import { useNavigate, useParams } from "react-router-dom";
import tokenAwareFetcher from '../../../utils/tokenAwareFetcher';
import TextEdit from "../../molecules/TextEdit";
import { FaBuilding, FaWhatsapp, FaWpforms } from "react-icons/fa6";
import { useSignal } from "@preact/signals-react";
import { FaMailBulk } from "react-icons/fa";
import SelectFilter from "../../organisms/SelectFilter";
import ContactRecipientPicker from "./ContactRecipientPicker";
import MultiEmailInput from "./MultiEmailInput";
import CopyPasteTable from "../../organisms/CopyPasteTable";

import { toast } from "react-toastify";
import { ErrorField } from '../../../types/ErrorField';
import { getErrorMessage, hasErrorMessage, removeErrorField } from '../../../utils/formErrorUtils';
import useFetch from "../../../hooks/useFetch";
import { NotifyMailTemplate, NotifyTemplate } from "../../../types/NotificationTemplate";

import DayPicker from "../../molecules/DayPicker";
import MaskEdit from "../../organisms/MaskEdit";
import CurrencyInput from "../../molecules/CurrencyInput";

import NumberEdit from "../../molecules/NumberEdit";
import RepositoryFileSelect from "../../organisms/RepositoryFileSelect";

import { BucketType } from "../../../utils/constants";
import appConfig from "../../../config/app-config";

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const NOTIFICATION_URL = `${COMMUNICATION_ENV.address}/notifications`;
const API_KEY = COMMUNICATION_ENV.key;
const fetcher = tokenAwareFetcher(COMMUNICATION_ENV.address, API_KEY);

enum MediaType {
    WPP = "WPP",
    MAIL = "MAIL",
}

type CreationType = {
    type: "batch" | "single";
    pageTitle: string;
    buttonSaveDisabled: boolean;
}

type SendOption = "immediate" | "later";
export type Variable = {
    field: string;
    value: string;
    format: string;
}

type Form = {
    sendOption: SendOption;
    operation: string;
    contactName: string;
    phoneNumber: string;
    email: string;
    cc: string[];
    bcc: string[];
    recipients: string[];
    variables: Variable[];
    scheduledDateType: string;
    template: NotifyTemplate;
    notifications?: [];
}

const BatchNotificationForm: React.FC = () => {
    const navigate = useNavigate();
    const { scheduledType, targetDate } = useParams();

    const creationtype = useSignal<CreationType>({
        type: "single",
        pageTitle: "Criar notificação",
        buttonSaveDisabled: false,
    });

    const { isLoading, data, error, isTokenError } = useFetch(`${NOTIFICATION_URL}/page-form/${scheduledType}`, [{ key: 'x-api-key', value: API_KEY }]);

    const mediaType = (data?.media as string | undefined) ?? scheduledType?.split("_")[0];

    const isLoadingForm = useSignal<boolean>(false);
    const errorFields = useSignal<ErrorField[]>([]);
    const apiVersion = useSignal('');

    const form = useSignal<Form>({
        sendOption: "later",
        operation: "",
        contactName: "",
        phoneNumber: "",
        email: "",
        cc: [],
        bcc: [],
        recipients: [],
        variables: [],
        scheduledDateType: "fixed",
        template: {
            description: "",
            variables: [],
            batchHeaders: [],
            sendOption: ["immediate", "later"],
        } as NotifyTemplate,
    });

    if (isTokenError) {
        navigate("/denied");
    }

    if (error) {
        navigate("/network-error");
    }

    useEffect(() => {
        if (data) {
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;

            form.value = {
                ...form.value,
                template: {
                    ...form.value.template,
                    description: data.description,
                    sendOption: data.sendOption,
                    batchHeaders: data.batchHeaders,
                    variables: data.variables,
                    groupName: data.groupName,

                    ...(mediaType === MediaType.MAIL && {
                        subject: data.subject,
                        from: data.from,
                        replyTo: data.replyTo,
                        showCcField: data.showCcField,
                        showBccField: data.showBccField,
                        canEditBccField: data.canEditBccField,
                        bcc: data.bcc,
                    }),
                },
                variables: data.variables.map((variable: Variable) => ({
                    field: variable.field,
                    value: "",
                    format: variable.format,
                })),
            };
        }
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleEvent = (event: CustomEvent) => {
            form.value = {
                ...form.value,
                notifications: event.detail,
            };
        };

        window.addEventListener("CopyPasteTable:jsonGenerated", handleEvent as EventListener);
        return () => window.removeEventListener("CopyPasteTable:jsonGenerated", handleEvent as EventListener);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleErrors = (event: CustomEvent) => {
            if (creationtype.value.type === "single") return;

            creationtype.value.buttonSaveDisabled = true;
            const errors = event.detail;
            if (Array.isArray(errors) && errors.length === 0 && form.value.notifications && form.value.notifications.length > 0) {
                creationtype.value.buttonSaveDisabled = false;
            }
        };

        window.addEventListener("CopyPasteTable:errors", handleErrors as EventListener);
        return () => window.removeEventListener("CopyPasteTable:errors", handleErrors as EventListener);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const clearData = () => {
        window.dispatchEvent(new Event("CopyPasteTable:clearData"));
    };

    const changeCreationType = (type: "batch" | "single") => {
        creationtype.value = {
            type,
            pageTitle: type === "batch" ? "Criar notificação em lote" : "Criar notificação",
            buttonSaveDisabled: type === "batch",
        };
    };

    const submitForm = async () => {
        if (creationtype.value.buttonSaveDisabled) {
            toast.error("Por favor, corrija os erros antes de enviar o formulário.");
            return;
        }

        if (creationtype.value.type === "single" && form.value.recipients.length === 0) {
            toast.error("Selecione ao menos um destinatário.");
            return;
        }

        isLoadingForm.value = true;

        const buildVariables = () =>
            form.value.variables.reduce((acc, variable) => {
                let { value } = variable;
                if (variable.format === 'currency') {
                    const num = Number(value);
                    if (!isNaN(num)) {
                        value = 'R$ ' + num.toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        });
                    }
                }
                acc[variable.field] = value;
                return acc;
            }, {} as Record<string, string>);

        const singleBase = {
            scheduledType,
            targetDate,
            sendOption: form.value.sendOption,
            mediaType,
            operation: form.value.operation,
            contactName: form.value.contactName,
            variables: buildVariables(),
        };

        try {
            if (creationtype.value.type === "batch") {
                const path = `${NOTIFICATION_URL}/batch`.replace(COMMUNICATION_ENV.address, '');
                const response = await fetcher.post(path, {
                    scheduledType,
                    targetDate,
                    sendOption: form.value.sendOption,
                    mediaType,
                    notifications: form.value.notifications,
                });
                if (response.ok) {
                    toast.info("Notificações criadas com sucesso!");
                    navigate(-1);
                } else {
                    const { result } = await response.json();
                    toast.error("Algumas notificações não foram criadas. Verifique os erros na lista!");
                    window.dispatchEvent(new CustomEvent("CopyPasteTable:setErrors", { detail: result }));
                }
                isLoadingForm.value = false;
                return;
            }

            const path = NOTIFICATION_URL.replace(COMMUNICATION_ENV.address, '');
            const bodies = mediaType === MediaType.MAIL
                ? [{ ...singleBase, to: form.value.recipients, cc: form.value.cc, bcc: form.value.bcc }]
                : form.value.recipients.map(phone => ({ ...singleBase, phone }));

            const responses = await Promise.all(bodies.map(body => fetcher.post(path, body)));
            const failed = responses.find(r => !r.ok);

            if (!failed) {
                toast.info(bodies.length > 1 ? "Notificações criadas com sucesso!" : "Notificação criada com sucesso!");
                navigate(-1);
            } else {
                const { errors, message } = await failed.json();
                toast.error(message);
                errorFields.value = errors ? errors.map((error: any) => ({ fieldName: error.field, message: error.message })) : [];
            }
        } catch (error) {
            toast.error("Ocorreu um erro com a rede. Tente novamente mais tarde.");
        }
        isLoadingForm.value = false;
    };

    const changeForm = (field: string, value: string) => {
        form.value = {
            ...form.value,
            [field]: value,
        };
        removeErrorField(errorFields.value, field);
    };

    const cancelForm = () => {
        navigate(-1);
    };

    const changeFormVariables = (field: string, value: string) => {
        const newVariables = form.value.variables.map(variable => {
            if (variable.field === field) {
                return { ...variable, value };
            }
            return variable;
        });
        form.value = { ...form.value, variables: newVariables };
    };

    return (
        <PageForm
            pageTitle={creationtype.value.pageTitle}
            apiVersion={apiVersion.value}
            isLoading={isLoading || isLoadingForm.value}
            breadcrumb={[
                { label: "Notificações do dia", url: "/communication/notification/0/100/contactFullName/ASC/" },
                { label: creationtype.value.pageTitle, url: "#" },
            ]}
            controlSlot={(
                <div className="flex space-x-4 justify-end m-2">
                    <button
                        className="bg-zinc-600 text-white px-4 py-2 rounded inline-flex items-center hover:bg-zinc-800"
                        onClick={() => changeCreationType(creationtype.value.type === "batch" ? "single" : "batch")}
                        disabled={isLoading || isLoadingForm.value}
                    >
                        <FaWpforms className="mr-2" />
                        {creationtype.value.type === "batch" ? "Simples" : "Em lote"}
                    </button>
                    {creationtype.value.type === "batch" && (
                        <button
                            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700"
                            onClick={clearData}
                            disabled={isLoading || isLoadingForm.value}
                        >
                            Limpar Dados
                        </button>
                    )}
                    <button
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded'
                        onClick={cancelForm}
                        title="Cancelar registro"
                        disabled={isLoading || isLoadingForm.value}
                    >
                        Cancelar
                    </button>
                    <button
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={submitForm}
                        title="Salvar registro"
                        disabled={isLoading || isLoadingForm.value || creationtype.value.buttonSaveDisabled}
                    >
                        Salvar
                    </button>
                </div>
            )}
        >
            <div className="container mx-auto p-4">
                <div className="grid grid-cols-12 gap-4 mb-4">
                    <div className={`${creationtype.value.type === "batch" ? "col-span-5" : "col-span-6"}`}>
                        <TextEdit label="Tipo de notificação:" value={form.value.template.description} disabled readOnly />
                    </div>
                    {creationtype.value.type === "batch" && (
                        <div className="col-span-2">
                            <SelectFilter
                                id="scheduledDateType"
                                label="Opção de agendamento:"
                                value={form.value.scheduledDateType}
                                options={[
                                    { value: "fixed", label: "Data fixa" },
                                    { value: "fromFile", label: "Data personalizada", disabled: true },
                                ]}
                                onChange={(value) => changeForm("scheduledDateType", value)}
                            />
                        </div>
                    )}

                    <div className={`${creationtype.value.type === "batch" ? "col-span-2" : "col-span-3"}`}>
                        <TextEdit label="Data do agendamento:"
                            className={`${form.value.scheduledDateType === "fromFile" && creationtype.value.type === "batch" ? "line-through text-gray-200" : ""}`}
                            value={targetDate?.split("-").reverse().join("/")} disabled readOnly />
                    </div>

                    <div className="row-span-2 col-span-3 p-4 bg-white rounded-lg shadow-md border border-gray-200">
                        {mediaType === MediaType.WPP && (
                            <FaWhatsapp className="col-auto text-3xl text-green-700 float-right mt-2" />
                        )}
                        {mediaType === MediaType.MAIL && (
                            <FaMailBulk className="col-auto text-3xl text-blue-700 float-right mt-2" />
                        )}

                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="sendOption"
                                value="later"
                                checked={form.value.sendOption === "later"}
                                onChange={() => changeForm("sendOption", "later")}
                                className="hidden"
                            />
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center 
                                ${form.value.sendOption === "later" ? "border-blue-500" : "border-gray-400"}`}>
                                {form.value.sendOption === "later" && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full"></div>}
                            </div>
                            <span className="text-gray-900 text-sm font-medium">Gravar para enviar depois</span>
                        </label>

                        <label className="flex items-center space-x-2 cursor-pointer mt-1">
                            <input
                                type="radio"
                                name="sendOption"
                                value="immediate"
                                checked={form.value.sendOption === "immediate"}
                                onChange={() => changeForm("sendOption", "immediate")}
                                className="hidden"
                                disabled={true}
                            />
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center 
                                ${form.value.sendOption === "immediate" ? "border-blue-500" : "border-gray-400"}`}>
                                {form.value.sendOption === "immediate" && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full"></div>}
                            </div>
                            <span className="text-gray-900 text-sm font-medium">Enviar imediatamente</span>
                        </label>
                    </div>

                    {mediaType === MediaType.MAIL && (
                        <>
                            <div className="col-span-4">
                                <TextEdit label="Titulo da mensagem:" value={(form.value.template as NotifyMailTemplate).subject} disabled readOnly />
                            </div>
                            <div className="col-span-5 grid grid-cols-2 gap-4">
                                <div className="col-span-1">
                                    <TextEdit label="De:" value={(form.value.template as NotifyMailTemplate).from} disabled readOnly />
                                </div>
                                <div className="col-span-1">
                                    <TextEdit
                                        label="Responder para:"
                                        value={(Array.isArray((form.value.template as NotifyMailTemplate).replyTo)
                                            ? (form.value.template as NotifyMailTemplate).replyTo.join(", ")
                                            : (form.value.template as NotifyMailTemplate).replyTo)}
                                        disabled
                                        readOnly
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
                {creationtype.value.type === "batch" ? (
                    <div className="grid grid-cols-1">
                        <CopyPasteTable fixedHeaders={form.value.template.batchHeaders} allowDuplicates={true} />
                    </div>

                ) : (
                    <>
                        <div className="mt-4 pt-4">
                            <ContactRecipientPicker
                                channel={mediaType as "MAIL" | "WPP"}
                                templateGroup={form.value.template.groupName}
                                onContactChange={(name) => changeForm("contactName", name)}
                                onRecipientsChange={(values) => {
                                    form.value = { ...form.value, recipients: values };
                                }}
                            />
                        </div>
                        {mediaType === MediaType.MAIL && (form.value.template as NotifyMailTemplate).showCcField && (
                            <div className="mt-4">
                                <MultiEmailInput
                                    label="Cc:"
                                    value={form.value.cc}
                                    onChange={(emails) => { form.value = { ...form.value, cc: emails }; }}
                                    placeholder="Digite um email e tecle Enter"
                                />
                            </div>
                        )}
                        {mediaType === MediaType.MAIL && (form.value.template as NotifyMailTemplate).showBccField && (
                            <div className="mt-4">
                                <MultiEmailInput
                                    label="Bcc:"
                                    value={form.value.bcc}
                                    onChange={(emails) => { form.value = { ...form.value, bcc: emails }; }}
                                    placeholder="Digite um email e tecle Enter"
                                />
                            </div>
                        )}
                        <div className="grid grid-cols-12 gap-4 mt-4">
                            {form.value.template.variables.map((variable, index) => (
                                <div key={index} className="col-start-1 col-span-5">
                                    {variable.format === "text" && (
                                        <TextEdit
                                            label={`${variable.label}:`}
                                            value={form.value.variables.find(v => v.field === variable.field)?.value || ""}
                                            onChange={(e) => changeFormVariables(variable.field, e.target.value)}
                                            errorMessage={hasErrorMessage(errorFields.value, variable.field) ? getErrorMessage(errorFields.value, variable.field) : ""}
                                            placeholder=""
                                            maxLength={variable.size || 100}
                                        />)}

                                    {variable.format === "number" && (
                                        <NumberEdit
                                            label={`${variable.label}:`}
                                            value={form.value.variables.find(v => v.field === variable.field)?.value || ""}
                                            onChange={(e) => changeFormVariables(variable.field, e.target.value)}
                                            errorMessage={hasErrorMessage(errorFields.value, variable.field) ? getErrorMessage(errorFields.value, variable.field) : ""}
                                            placeholder=""
                                            maxLength={variable.size || 100}
                                        />
                                    )}

                                    {variable.format === "date" && (
                                        <DayPicker
                                            label={`${variable.label}:`}
                                            value={form.value.variables.find(v => v.field === variable.field)?.value || ""}
                                            onChange={(value) => changeFormVariables(variable.field, value)}
                                            errorMessage={hasErrorMessage(errorFields.value, variable.field) ? getErrorMessage(errorFields.value, variable.field) : ""}
                                        />
                                    )}

                                    {variable.format === "currency" && (
                                        <CurrencyInput
                                            label={`${variable.label}:`}
                                            value={form.value.variables.find(v => v.field === variable.field)?.value || ""}
                                            onChange={(value) => changeFormVariables(variable.field, value.toString())}
                                            errorMessage={hasErrorMessage(errorFields.value, variable.field) ? getErrorMessage(errorFields.value, variable.field) : ""}
                                            prefix="R$"
                                            fractionDigits={2}
                                            placeholder=""
                                        />
                                    )}

                                    {variable.format === "govTaxId" && (
                                        <MaskEdit
                                            replacement={{ "_": /\d/ }}
                                            labelClassName="ml-4"
                                            label={`${variable.label}:`}
                                            value={form.value.variables.find(v => v.field === variable.field)?.value || ""}
                                            onChange={(e) => changeFormVariables(variable.field, e.target.value)}
                                            errorMessage={hasErrorMessage(errorFields.value, variable.field) ? getErrorMessage(errorFields.value, variable.field) : ""}
                                            mask="__.___.___/____-__"
                                            prefix={<FaBuilding className="text-gray-500 mr-2" />}
                                        />
                                    )}

                                    {variable.format === "fileUrl" && (
                                        <RepositoryFileSelect
                                            bucketS3Account={`${appConfig.awsAccountId}`}
                                            bucketsActives={[BucketType.BOLETO]}
                                            value={form.value.variables.find(v => v.field === variable.field)?.value || ""}
                                            onSelectChange={(value) => changeFormVariables(variable.field, value)}
                                            onTextChange={(value) => changeFormVariables(variable.field, value)}
                                            onDownload={() => console.log("Download file triggered")}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

        </PageForm>
    );
};

export default BatchNotificationForm;